/** Tell the other tabs of this browser that the user has seen the notes
 *  up to a version, so an open What's new dialog elsewhere closes
 *  instead of asking again. Feature-detected: without BroadcastChannel
 *  each tab just keeps its own dialog (the server value is monotonic,
 *  so a second acknowledgement is harmless).
 *
 *  One channel per tab: a BroadcastChannel never receives its own
 *  messages, so this tab doesn't hear its own acknowledgement. */

export const WHATS_NEW_CHANNEL = "rw.whats-new";

type SeenMessage = { type: "seen"; version: string };

let channel: BroadcastChannel | null | undefined;

function shared(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  if (typeof BroadcastChannel !== "function") {
    channel = null;
    return channel;
  }
  try {
    channel = new BroadcastChannel(WHATS_NEW_CHANNEL);
    // Node's (vitest) keeps the process alive otherwise; browsers have no unref.
    (channel as { unref?: () => void }).unref?.();
  } catch {
    channel = null;
  }
  return channel;
}

function isSeen(data: unknown): data is SeenMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    (data as SeenMessage).type === "seen" &&
    typeof (data as SeenMessage).version === "string"
  );
}

export function postSeen(version: string): void {
  try {
    shared()?.postMessage({ type: "seen", version } satisfies SeenMessage);
  } catch {
    // A closed or broken channel: the other tabs find out on their next load.
  }
}

/** Call ``onSeen`` when another tab acknowledges; returns the unsubscribe. */
export function subscribeSeen(onSeen: (version: string) => void): () => void {
  const ch = shared();
  if (!ch) return () => undefined;
  const listener = (event: MessageEvent) => {
    if (isSeen(event.data)) onSeen(event.data.version);
  };
  ch.addEventListener("message", listener);
  return () => ch.removeEventListener("message", listener);
}

/** Tests only: drop the channel so the next use opens a fresh one. */
export function closeWhatsNewChannel(): void {
  try {
    channel?.close();
  } catch {
    // Already closed.
  }
  channel = undefined;
}
